import type { TemplateRegion } from "../types";

/**
 * Shirt-space ↔ texture-space coordinate helpers.
 *
 * DCL wearable UVs are often rotated / mirrored in the texture relative
 * to the shirt's natural orientation (e.g. our long sleeve is rotated
 * ~45°). That causes raw texture-pixel X/Y sliders to feel diagonal.
 *
 * Each `TemplateRegion` can carry two unit vectors:
 *   - `right`: where the user's right points in texture pixels when they
 *              look at this side of the shirt in the 3D viewer.
 *   - `up`:    where the user's up points in texture pixels.
 *
 * The helpers below convert between a "shirt space" offset from the
 * box centre (what the user cares about) and absolute texture pixel
 * coordinates (what we store in `ArtworkLayer.x/y` and what the
 * composer draws with).
 */

const DEFAULT_RIGHT: readonly [number, number] = [1, 0];
const DEFAULT_UP: readonly [number, number] = [0, -1];

export interface ShirtAxes {
  right: readonly [number, number];
  up: readonly [number, number];
  cx: number;
  cy: number;
}

export function getShirtAxes(box: TemplateRegion): ShirtAxes {
  return {
    right: box.right ?? DEFAULT_RIGHT,
    up: box.up ?? DEFAULT_UP,
    cx: box.x + box.width / 2,
    cy: box.y + box.height / 2,
  };
}

/**
 * Convert a shirt-space offset `(sx, sy)` (both in texture pixels, but
 * measured along the shirt's right / up axes) to absolute texture pixel
 * coordinates.
 */
export function shirtToTexture(
  sx: number,
  sy: number,
  axes: ShirtAxes,
): { x: number; y: number } {
  return {
    x: axes.cx + sx * axes.right[0] + sy * axes.up[0],
    y: axes.cy + sx * axes.right[1] + sy * axes.up[1],
  };
}

/**
 * Inverse of {@link shirtToTexture}. Expresses an absolute texture
 * pixel position as a shirt-space offset from the box centre.
 */
export function textureToShirt(
  x: number,
  y: number,
  axes: ShirtAxes,
): { sx: number; sy: number } {
  const dx = x - axes.cx;
  const dy = y - axes.cy;
  const [rx, ry] = axes.right;
  const [ux, uy] = axes.up;
  const det = rx * uy - ry * ux;
  if (Math.abs(det) < 1e-9) {
    return { sx: dx, sy: dy };
  }
  return {
    sx: (uy * dx - ux * dy) / det,
    sy: (-ry * dx + rx * dy) / det,
  };
}

/**
 * Rotation in radians that aligns an image's natural +X axis with the
 * shirt's "right" direction in texture space. Adding this to the
 * layer's own rotation makes the artwork appear upright on the 3D
 * shirt even when the UV is rotated.
 */
export function shirtRotationRad(axes: ShirtAxes): number {
  return Math.atan2(axes.right[1], axes.right[0]);
}
